import {Component, OnInit, ViewChild} from '@angular/core';
import {ApiResponse, BaseModalComponent, GLOBAL, LeModalService, LeToastrService, PRIVILEGES} from 'common-shared';
import {ManufacturingItemService} from "../manufacturing-item.service";
import {ItemsQuery, ManufacturingItemModel} from "web-shared";
import {ManufacturingItemEditComponent} from "../manufacturing-item-edit/manufacturing-item-edit.component";
import {BehaviorSubject, Subject} from "rxjs";
import {AutoSaveService} from "../auto-save/auto-save.service";
import {debounceTime, filter, takeUntil} from "rxjs/operators";
import {marker} from "@jsverse/transloco-keys-manager/marker";
import {
  ConfirmFinalModificationModalComponent
} from "../../../common/confirm-final-modification-modal/confirm-final-modification-modal.component";
import {instanceToInstance} from "class-transformer";

@Component({
  selector: 'mes-manufacturing-item-edit-modal',
  templateUrl: './manufacturing-item-edit-modal.component.html'
})
export class ManufacturingItemEditModalComponent extends BaseModalComponent implements OnInit {
  entityId: string;
  entityName: string;
  response: ApiResponse<any>;
  item: ManufacturingItemModel;
  isPristine$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  currentTab: 'general' | 'ime';
  scrollIntoViewId: string;
  @ViewChild(ManufacturingItemEditComponent, {static: true}) editComponent: ManufacturingItemEditComponent;
  shouldResetSaveState$: Subject<boolean> = new Subject<boolean>();
  wasValidated$: Subject<boolean> = new Subject<boolean>();
  Classificators = GLOBAL.Classificators;
  protected readonly PRIVILEGES = PRIVILEGES;
  listenCacheUpdate = false;

  constructor(private manufacturingItemService: ManufacturingItemService, private autoSaveService: AutoSaveService, private toastrService: LeToastrService, private itemsQuery: ItemsQuery, protected modalService: LeModalService) {
    super();
  }

  ngOnInit() {
    if (this.entityId) {
      this.manufacturingItemService.loadOne(this.entityId).subscribe(item => {
        this.item = item
        this.tryGetRestoredItem();
        this.notifyIfEntityIsUpdatedElsewhere();
      });
    } else {
      this.item = this.item ?? ManufacturingItemModel.CreateDefault({Code: this.entityName, Name: this.entityName});
    }
  }

  private tryGetRestoredItem() {
    const restoredItem = this.autoSaveService.tryGetRestoredItem(this.item.Id);
    if (restoredItem) {
      this.autoSaveService.confirmItemRestore(this.item, restoredItem).then(confirmed => {
        if (confirmed) {
          this.item = restoredItem.Content;
        }
      });
    }
  }

  onChanged(isPristine: boolean) {
    this.isPristine$.next(isPristine);
    setTimeout(() => {this.autoSaveService.updateItem(isPristine, this.item)});
  }

  save(withClose: boolean = true) {
    this.wasValidated$.next(true);
    if (this.item.isValid()) {
      this.listenCacheUpdate = false;
      if (this.item.StatusClv == GLOBAL.Classificators.ItemStatus.Final) {
        this.modalService.show(ConfirmFinalModificationModalComponent).onHide$.subscribe(value => {
          if (value.result == 'save') {
            this.saveItem(withClose);
          } else if (value.result == 'create-draft') {
            this.copyAsDraft(withClose);
          }
        })
      } else {
        this.saveItem(withClose);
      }
    } else {
      this.currentTab = 'general';
    }
  }

  saveItem = (withClose: boolean) => {
    this.manufacturingItemService.save(this.item).subscribe({
      next: value => {
        this.entityId = value.Id;
        this.autoSaveService.removeItem(value.Id);
        this.apply(null, 'save');
        if (withClose) {
          this.close(null, 'Save');
        } else {
          this.item = value;
          this.editComponent.initialize(this.item);
          this.shouldResetSaveState$.next(true);
        }
      }, error: value1 => {
        this.response = value1.error;
      }
    });
  }

  copyAsDraft(withClose: boolean) {
    this.manufacturingItemService.copyAsDraft(this.item.Id).subscribe(value => {
      if (withClose) {
        this.close(null,'Save');
      } else {
        this.manufacturingItemService.loadOne(value.toString()).subscribe(
          result => {
            this.updateItemAndLoadIme(result)
          }
        );
      }
    });
  }

  delete() {
    this.manufacturingItemService.remove(this.item).subscribe({
      next: () => {
        this.autoSaveService.removeItem(this.item.Id);
        this.item = null;
        this.entityId = null;
        this.close(null, 'Delete');
      }, error: value1 => {
        this.response = value1.error;
      }
    });
  }

  add() {
    this.wasValidated$.next(true);
    if (this.item.isValid()) {
      this.shouldResetSaveState$.next(true);
      this.manufacturingItemService.addNew(this.item).subscribe({next: value => {
        this.entityId = value.Id;
        this.item = value;
        this.autoSaveService.removeItem(this.item.Id);
        this.close(null, 'Save');
      }, error: value1 => {
        this.response = value1.error;
      }});
    } else {
      this.currentTab = 'general';
    }
  }

  updateItemAndLoadIme(item: ManufacturingItemModel): void {
    this.item = instanceToInstance(item);
    this.editComponent.initialize(this.item);
  }

  private notifyIfEntityIsUpdatedElsewhere() {
    this.itemsQuery.selectEntity(this.item.Id).pipe(takeUntil(this.componentDestroyed), debounceTime(1000), filter(item => {
      if (!item) return false;
      const listenCacheUpdate = this.listenCacheUpdate;
      if (!listenCacheUpdate) {
        this.listenCacheUpdate = true;
      }
      return listenCacheUpdate;
    })).subscribe(item => {
      this.toastrService.translatedWarning(marker('ime.manufacturing.manufacturing-item.Item has been updated elsewhere. Reload page'), marker('ime.manufacturing.manufacturing-item.Reload'));
    });
  }
}
